import { findMaxSecondMarker } from "../../../../helpers";

export const convertHalfPitchCoordinates = ({
  coordX,
  coordY,
  fieldWidth,
  fieldHeight,
}) => {
  const scaleX = fieldHeight / 52.5;
  const scaleY = fieldWidth / 68;

  const x = Math.min(Math.max(coordY * scaleX, 0), fieldWidth);
  const y = Math.min(Math.max(coordX * scaleY - 30, 0), fieldHeight);

  return {
    x: Math.round(x),
    y: Math.round(y),
  };
};

export const createGoalMouthConverter = ({
  imageWidthPx = 649,
  imageHeightPx = 482,
  goalWidthMeters = 7.32,
  goalHeightMeters = 2.44,
  verticalPaddingMeters = 1.06,
  horizontalPaddingMeters = 1.34,
} = {}) => {
  const goalCenterX = imageWidthPx / 2;
  const goalCenterY = imageHeightPx / 2;

  const paddingTopBottom = verticalPaddingMeters * 2;
  const paddingLeftRight = horizontalPaddingMeters * 2;
  const goalWidthWithPaddings = goalWidthMeters + paddingLeftRight;
  const goalHeightWithPaddings = goalHeightMeters + paddingTopBottom;

  const goalScaleX = imageWidthPx / goalWidthWithPaddings;
  const goalScaleY = imageHeightPx / goalHeightWithPaddings;

  const topBottomPadding = goalCenterY - verticalPaddingMeters * goalScaleY;
  const leftRightPadding = goalCenterX - horizontalPaddingMeters * goalScaleX;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  return (x, y) => ({
    x: x * goalScaleX + leftRightPadding + horizontalPaddingMeters * goalScaleX,
    y:
      (goalHeightMeters - clamp(y, 0, goalHeightMeters)) * goalScaleY +
      topBottomPadding,
  });
};

export const countHalfPitchZones = ({
  events,
  convertCoordinatesWithPadding,
  mirrorCoordinates,
}) => {
  const zoneWidth = 160;
  const firstDivider = 85 + zoneWidth;
  const secondDivider = 85 + zoneWidth * 2;

  const counts = {
    leftTop: 0,
    middleTop: 0,
    rightTop: 0,
    bottom: 0,
  };

  events?.forEach((event) => {
    const { coord_x, coord_y } = mirrorCoordinates(event);
    const position = convertCoordinatesWithPadding(coord_x, coord_y);

    if (position.y <= 482 / 2) {
      // Assign the side padding to its nearest zone too. Otherwise an event in
      // either outer strip is included in the denominator but not in any zone.
      if (position.x <= firstDivider) {
        counts.leftTop += 1;
      } else if (position.x <= secondDivider) {
        counts.middleTop += 1;
      } else {
        counts.rightTop += 1;
      }
    } else {
      counts.bottom += 1;
    }
  });

  return counts;
};

// Displayed zone percentages must add up to exactly 100. Rounding each raw
// percentage independently can otherwise leave a one- or two-point gap.
export const calculateZonePercentages = (counts = {}) => {
  const entries = Object.entries(counts).map(([key, value]) => [
    key,
    Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0,
  ]);
  const total = entries.reduce((sum, [, value]) => sum + value, 0);

  if (total <= 0) {
    return Object.fromEntries(entries.map(([key]) => [key, 0]));
  }

  const rounded = entries.map(([key, value], index) => {
    const raw = (value / total) * 100;
    const floor = Math.floor(raw);

    return { key, index, value: floor, remainder: raw - floor };
  });
  const remaining = 100 - rounded.reduce((sum, item) => sum + item.value, 0);

  rounded
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
    .slice(0, remaining)
    .forEach((item) => {
      item.value += 1;
    });

  return Object.fromEntries(rounded.map(({ key, value }) => [key, value]));
};

export const countGoalMouthZones = ({
  events,
  convertCoordinatesGoalWithPadding,
  mirrorCoordinates,
}) => {
  const goalCounts = {
    leftTop: 0,
    middleTop: 0,
    rightTop: 0,
    leftBottom: 0,
    middleBottom: 0,
    rightBottom: 0,
  };

  const calcCountEventsOnGoalZones = (x, y) => {
    if (y <= 245 && x <= 240) {
      goalCounts.leftTop += 1;
    } else if (y <= 245 && x > 240 && x <= 398) {
      goalCounts.middleTop += 1;
    } else if (y <= 245 && x > 398) {
      goalCounts.rightTop += 1;
    } else if (y > 245 && x <= 240) {
      goalCounts.leftBottom += 1;
    } else if (y > 245 && x > 240 && x <= 398) {
      goalCounts.middleBottom += 1;
    } else if (y > 245 && x > 398) {
      goalCounts.rightBottom += 1;
    }
  };

  events?.forEach((event) => {
    const { gate_coord_x, gate_coord_y } = mirrorCoordinates(event);
    const goalPosition = convertCoordinatesGoalWithPadding(
      gate_coord_x,
      gate_coord_y
    );

    calcCountEventsOnGoalZones(goalPosition.x || 0, goalPosition.y || 0);
  });

  return goalCounts;
};

export const getTimelineMaxMinutes = (events, fallbackMinutes = 90) => {
  const max = findMaxSecondMarker(events || []);
  const nextMax = Math.ceil(+max / 60 / 5) * 5;

  return nextMax > 0 ? nextMax : fallbackMinutes;
};
